import React from 'react';
import { Classable, HasChildren } from '@shapeable/types';
import styled, { css } from 'styled-components';
import { breakpoints, ColorTokens, theme } from '@shapeable/theme';
import { PageContextDefinition, useEmbedComponents, EmbedComponentsProvider, LinkPathTransformerProvider, useEntity, useAuth, useSiteMenuBar, ViewProvider, UiProvider, useOrganisations, useReady, DarkMode, ComponentPropsProvider, UiConfig, TrendCard, TopicCard, EntityValuePill, DataHook, EntityCard, CitationCard, FileAssetCard, EntityExplorerLayout, useContributeForm, SiteFooterContext } from '@shapeable/ui';
import * as pageLayouts from './page-layouts';
import * as sliceLayouts from './slice-layouts';
import { LANG_STRINGS } from '../lang';
import { themeOverrides, LayoutStyles, COLOR_VALUE_MINE_DEVELOPMENT, CORE_7, COLOR_VALUE_MINERAL_PROCESSING, CORE_3, ICAA } from '../theme';
import { CitationsProvider } from '../gatsby/providers/citations-provider';
import { TrendsExplorerProvider } from '../gatsby/providers/trends-explorer-provider';
import { InnovationsProvider } from '../gatsby/providers/innovations-provider';
import { CommodityGroupsProvider } from '../gatsby/providers/commodity-groups-provider';
import { OrganisationsProvider } from '../gatsby/providers/organisations-provider';
import { PeopleProvider } from '../gatsby/providers/people-provider';
import { TopicsProvider } from '../gatsby/providers/topics-provider';
import { TrendsProvider } from '../gatsby/providers/trends-provider';
import { CatalystsProvider } from '../gatsby/providers/catalysts-provider';
import { HorizonTypesProvider } from '../gatsby/providers/horizon-types-provider';
import { ImageEmbed } from './elements/image-embed';
import { TopicsEmbed } from './entities/topics-embed';
import { useValueChainMap } from '../hooks/use-value-chain-map';
import { menuItemIsAvailable } from '../utils/menu-item-is-available';
import { isExplorerEntity } from '../utils/is-explorer-entity';
import { SidebarButtons } from './elements/sidebar-buttons';
import { CommoditiesProvider } from '../gatsby/providers/commodities-provider';
import { CatalystIcon } from './elements/catalyst-icon';
import { EconomiesProvider } from '../gatsby/providers/economies-provider';
import { TechnologiesProvider } from '../gatsby/providers/technologies-provider';
import { TechnologyIcon } from './elements/technology-icon';
import { PostsProvider } from '../gatsby/providers/posts-provider';
import { LayoutShell } from '@shapeable/ui';

import * as EntityPageQueries from '../graphql/details';
import * as EntityAllQueries from '../graphql/all';
import { CommodityIcon, ConnectionsNavigatorIcon, ConnectionsNavigatorIconGlyph, LightGlobeIcon } from '@shapeable/icons';
import { EntityMapPanel, MapConfig, MapProvider, MapState } from '@shapeable/maps';
import { DEFAULT_MAP_ENTITY_TYPE_NAMES } from '../data';
import { OrganisationIcon } from '@shapeable/icons';
import { GptConfig, GptProvider } from '@shapeable/gpt';
import { OrganisationTooltip } from './entities/organisation-tooltip';
import { useInnovations } from '../hooks/use-innovations';
import { LinkCard } from './entities/link-card';
import { useCatalysts } from '../hooks/use-catalyst';
import { TrendExplorerLayout } from './entities/trend-explorer-layout';
import { TopicExplorerLayout } from './entities/topic-explorer-layout';
import { ContributeFormPanel } from './elements/contact-form-panel';
import { InnovationPrioritiesProvider } from '../gatsby/providers/innovation-priorities-provider';
import { OrganisationTypesProvider } from '../gatsby/providers/organisation-types-provider';
import { SubTopicsEmbed } from './entities/sub-topics-embed';
import { SiteFooterLayout } from '@shapeable/ui';
import { includes } from 'lodash';
import { CommunityRequestsProvider } from '../gatsby/providers/community-requests-provider';
import { CommunityRequestTypesProvider } from '../gatsby/providers/community-request-types-provider';

const initialMapState: MapState = {
  isVisible: false,
  isInitialized: false,
  zoom: 2,
  entityTypeNames: DEFAULT_MAP_ENTITY_TYPE_NAMES,
  latitude: -5.441022303718128,
  longitude: 117.59765625000352,
};

export type LayoutPropTypes = Classable & HasChildren & {
  path?: string;
  data?: any;
  pageContext?: PageContextDefinition;
};

const LayoutDefaultProps: LayoutPropTypes = {
  
};

type ContributeFormPanelProps = {
  isVisible?: boolean;
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    ${LayoutStyles};
  `,
});

const FooterStyles = breakpoints({
  base: css`
  `
});

const BodyStyles = breakpoints({
  base: css`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  `,
});

const ContributeFormStyles = breakpoints({
  base: css`
    position: fixed;
    left: 100%;
    z-index: 800;
    transition: left 0.3s;
    width: 100%;
    height: 100%;
    top: 0;

    ${({ isVisible }: ContributeFormPanelProps) => isVisible && css`
      left: 0%;
    `}
  `,
  tablet: css`

  `,
  desktop: css`
    width: 50%;

    ${({ isVisible }: ContributeFormPanelProps) => isVisible && css`
      left: 50%;
    `}
  `,
});

// -------- Components -------->

const My = {
  Container: styled(LayoutShell)`${ContainerStyles}`,
  Body: styled.div`${BodyStyles}`,
  ContributeForm: styled(ContributeFormPanel)<ContributeFormPanelProps>`${ContributeFormStyles}`,
   
  
  Map: () => {
    const organisations = useOrganisations();
    return <EntityMapPanel items={organisations} />; 
  },
};

export const Layout: React.FC<LayoutPropTypes> = (props) => {
  const { className, children } = props;

  const embedComponents = useEmbedComponents();
  const contributeForm = useContributeForm();

  const entity = useEntity();

  const menuBar = useSiteMenuBar();
  const auth = useAuth();

  const valueChainMap = useValueChainMap();

  const linkPathTransformer = (path: string) => {
    if (path.match(/\/wikiore\-/)) {
      return path.replace(/^\/wikiore\-/, '/appendices/wikiore-');
    }
    return path;
  };

  const { isReady } = useReady();

  const isExplorer = includes(['Topic', 'Trend', 'Economy', 'Innovation', 'Person', 'Organisation', 'Post'], entity?.entityTypeName) || entity.entityPath === '/explorer' || entity.slug === 'gpt';

  const mapConfig: MapConfig = {
    initialMapState,
    entityTypeMapMarkers: {
      Economy: OrganisationIcon,
    },
    availableEntityTypeNames: ['Organisation'],
  };

  const gptConfig: GptConfig = {
    title: 'CopperAI',
  }
 
  const config: UiConfig = {
    layouts: { pageLayouts, sliceLayouts, defaultPageLayout: pageLayouts.PageLayoutDefault },
    langStrings: LANG_STRINGS,
    theme: themeOverrides, 
    sidebar: {
      isAvailable: () => auth.isAuthenticated && isExplorerEntity(entity),
      showFullscreenButton: false,
      color: ICAA, 
      onClick: () => { valueChainMap.toggle() },
      buttons: <SidebarButtons />
    },
    menuBar: {
      delay: 0,
      slideDownDuration: 0,
      showShadow: false,
    },
    cookieBannerFeaturesList: 
      <ul>
        <li><b>Contact Form Cookie:</b> enables the form feature (Netlify).</li>
      </ul>
    ,
    search: {
      requiresAuthentication: true,
      isAvailable: true,
      extraDataHooks: {
        Innovation: useInnovations as DataHook,
        Catalyst: useCatalysts as DataHook,
      },
    },
    header: {
      menuItemIsAvailable: menuItemIsAvailable(auth),
      showThemeMode: true,
    },
    embeds: {
      // ...embedComponents,
      // img: ImageEmbed,
      // Topics: TopicsEmbed,
    },
    // explorer: {
    //   defaultView: 'navigator',
    //   authenticationRequired: true,
    // },
    entity: {
      labels: {
        Post: 'Case Study',
        Trend: 'Value Chain',
      },
      pluralLabels: {
        Trend: 'Value Chains',
      },
      layouts: {
        Organisation: EntityExplorerLayout,
        Person: EntityExplorerLayout,
        Trend: TrendExplorerLayout,
        Topic: TopicExplorerLayout,
      },
      pageQueries: EntityPageQueries,
      /* @ts-ignore */
      // allQueries: EntityAllQueries,
      icons: {
        Catalyst: CatalystIcon,
        Technology: TechnologyIcon,
        Commodity: CommodityIcon,
        CommodityGroup: CommodityIcon,
      },
      tooltips: {
        Organisation: OrganisationTooltip,
      },
      cards: {
        FileAsset: FileAssetCard,
        Citation: CitationCard,
        Catalyst: EntityCard,
        Trend: TrendCard,
        Topic: TopicCard,
        Link: LinkCard,
      },
      values: {
        Technology: EntityValuePill,
        Barrier: EntityValuePill,
        ImpactResultType: EntityValuePill,
        Catalyst: EntityValuePill,
      },
      dataProviders: [
        TrendsExplorerProvider, InnovationsProvider,
        TechnologiesProvider, CommodityGroupsProvider,
        OrganisationsProvider, CommoditiesProvider,
        PeopleProvider, TopicsProvider, TrendsProvider,
        CatalystsProvider, HorizonTypesProvider, CitationsProvider,
        EconomiesProvider, PostsProvider, InnovationPrioritiesProvider, OrganisationTypesProvider,
        CommunityRequestTypesProvider, CommunityRequestsProvider,
      ],
    }, 
    componentProps: {
      SliceOnboardingTrendExpertiseGrid: {
        expertiseLevel: 'industry-expert',
      },
      ExplorerEntityDetailsLayout: {
        asideIsOffset: false,
      },
      GridBackButton: {
        icon: ConnectionsNavigatorIconGlyph,
      },
      SlideControls: {
        colors: {
          hover: {
            background: ICAA,
          }
        },
        arrowColors: {
          hover: {
            fill: ICAA,
          }
        }
      },
      MenuItem: {
        colors: {
          hover: {
            text: 'light',
            background: ICAA,
          },
        }
      },
      MenuItemLink: {
        colors: {
          hover: {
            text: '#88B68D',
            
          },
          active: {
            text: '#88B68D',
            border: '#88B68D',
          },
        }
      },
      NavigationMenuItem: {
        colors: {
          hover: {
            text: 'light',
          },
        }
      },
      SiteMenuBar: {
        colors: {
          background: '#43846E',
          hover: {
            text: 'bright',
          }
        }
      },
    }
  };

  
  return (
    <UiProvider value={config}>
    <ViewProvider>
    <LinkPathTransformerProvider value={linkPathTransformer}>
    
    <ComponentPropsProvider value={{
      SvgEntityNavigator: { holeRadius: 12 },
      SvgCircleNode: { angle: 90 },
      // SvgEntityNode: { circle: { colors: { active: { stroke: 'red' } } } },
    }}>
      { /* @ts-ignore */ }
      
    <EmbedComponentsProvider value={{
      ...embedComponents,
      img: ImageEmbed,
      Topics: TopicsEmbed,
      SubTopics: SubTopicsEmbed,
    }}>
      <GptProvider value={{
        ...gptConfig,
      }}>
      <MapProvider value={mapConfig}>
      <My.Container
        className={className}
        includeTooltip
        hideOverflow={false}
        tooltipProps={{
          backgroundColor: '#FFFFFF',
        }}
      >
        { 
          isReady && <My.Map /> 
        }
        <My.ContributeForm isVisible={contributeForm.isVisible} />
        <My.Body>
        {children}
        {
          !isExplorer && <SiteFooterLayout />
        }
        </My.Body>
      </My.Container>
      </MapProvider>
      </GptProvider>
    </EmbedComponentsProvider>

    </ComponentPropsProvider>
    </LinkPathTransformerProvider>
    </ViewProvider>
    </UiProvider>  
  );
  
  
};

Layout.defaultProps = LayoutDefaultProps;

